import React from "react";
import { ANALYTICS_EVENT, trackEvent } from "@/services/analytics/events";
import { FrillWidget } from "./frill-types";

export function useFrillWidget() {
    const widgetRef = React.useRef<FrillWidget | null>(null);
    const frillWidgetKey: string = import.meta.env.VITE_FRILL_UPDATES_WIDGET_KEY;

    React.useEffect(() => {
        if (!frillWidgetKey || !window.Frill) return undefined;

        let unsubscribeFromWidgetEvents: (() => void) | undefined;

        // This is an async function, but we're not going to use the resolved result directly,
        // instead we assign the promise to a variable so we can destroy it in the hook cleanup.
        // eslint-disable-next-line new-cap
        const widget = window.Frill("widget", {
            key: frillWidgetKey,
            callbacks: {
                onReady(readyWidget) {
                    widgetRef.current = readyWidget;
                    const unsubscribeOpen = readyWidget.events.on("open", () =>
                        trackEvent(ANALYTICS_EVENT.RELEASE_ANNOUNCEMENT_OPEN, {}),
                    );
                    const unsubscribeClose = readyWidget.events.on("close", () =>
                        trackEvent(ANALYTICS_EVENT.RELEASE_ANNOUNCEMENT_CLOSE, {}),
                    );
                    unsubscribeFromWidgetEvents = () => {
                        unsubscribeOpen();
                        unsubscribeClose();
                    };
                },
            },
        });

        return () => {
            widgetRef.current = null;
            unsubscribeFromWidgetEvents?.();
            // Calling destroy will also remove the widget from the DOM
            widget.destroy();
        };
    }, [frillWidgetKey]);

    return widgetRef;
}
